import {
  AuthorizationNotifier,
  AuthorizationRequest,
  AuthorizationServiceConfiguration,
  BaseTokenRequestHandler,
  FetchRequestor,
  GRANT_TYPE_AUTHORIZATION_CODE,
  GRANT_TYPE_REFRESH_TOKEN,
  RedirectRequestHandler,
  TokenRequest,
} from '@openid/appauth';
  
export class AuthFlow {
  requestor = new FetchRequestor();
  notifier = new AuthorizationNotifier();
  authHandler = new RedirectRequestHandler();
  tokenHandler = new BaseTokenRequestHandler(this.requestor);

  request;
  response;
  code;
  config;

  constructor(options) {
    this.options = options;
    this.authHandler.setAuthorizationNotifier(this.notifier);

    this.notifier.setAuthorizationListener((request, response) => {
      if (response) {
        this.request = request;
        this.response = response;
        this.code = response.code;
      }
    });
  }

  async fetchConfig() {
    this.config = await AuthorizationServiceConfiguration.fetchFromIssuer(
      `${this.options.authUrl}/oidc`,
      this.requestor,
    );
  }

  async start(state = {}, prompt = undefined) {
    if (!this.config) {
      await this.fetchConfig();
    }

    const request = new AuthorizationRequest(
      {
        client_id: this.options.clientId,
        redirect_uri: this.options.redirectUri,
        response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
        response_mode: 'hash',
        scope: 'openid offline_access',
        state: this.getStateString(state),
        extras: {
          prompt,
        }
      },
      undefined,
      true,
    );

    this.authHandler.performAuthorizationRequest(this.config, request);
  }

  async getToken(code) {
    if (!this.config) {
      await this.fetchConfig();
    }

    window.location.hash = window.location.search.replace(/^\?/, '');
    await this.authHandler.completeAuthorizationRequestIfPossible();

    const tokenRequest = new TokenRequest({
      client_id: this.options.clientId,
      grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
      redirect_uri: this.options.redirectUri,
      code: code || this.response?.code,
      extras: {
        code_verifier: this.request?.internal?.code_verifier || '',
      },
    });

    return this.tokenHandler.performTokenRequest(this.config, tokenRequest);
  }

  silentLogin(path = '/login?auto=true') {
    return new Promise((resolve, reject) => {
      const iframe = document.createElement('iframe');
      iframe.style = 'width: 100%; height: 600px; display: none;';
      document.body.appendChild(iframe);

      function onMessage(event) {
        const { data } = event;

        if (event.origin === window.location.origin) {
          if (data.type === 'auth.done') {
            destroy();
            resolve(data.token);
          } else if (data.type === 'auth.fail') {
            destroy();
            reject(new Error('Auth fail'));
          }
        }
      }

      const timeoutId = setTimeout(() => {
        destroy();
        reject(new Error('Silent login timeout'));
      }, 15000);

      function destroy() {
        clearTimeout(timeoutId);
        window.removeEventListener('message', onMessage, false);
      }
      
      iframe.src = path;
      window.addEventListener('message', onMessage, false);      
    });
  }

  useRefreshToken(refreshToken) {
    const tokenRequest = new TokenRequest({
      client_id: this.options.clientId,
      grant_type: GRANT_TYPE_REFRESH_TOKEN,
      redirect_uri: this.options.redirectUri,
      refresh_token: refreshToken,
    });

    return this.tokenHandler.performTokenRequest(this.config, tokenRequest);
  }

  getPayload(token) {
    try {
      const [,encodedPayload] = token.split('.');
      const payload = JSON.parse(atob(encodedPayload));
      return payload;
    } catch {
      return {};
    }
  }

  getStateString(state) {
    return btoa(JSON.stringify(state));
  }

  parseState(state) {
    try {
      return JSON.parse(atob(state));
    } catch (err) {
      return {};
    }
  }

  async revokeTokens(tokens) {
    const headers = {
      'content-type': 'application/x-www-form-urlencoded'
    };

    const body = {
      client_id: this.options.clientId,
    }

    if (tokens.accessToken) {
      await fetch(this.config.revocationEndpoint, {
        method: 'POST',
        headers,
        body: new URLSearchParams({ ...body, token: tokens.accessToken, token_type_hint: 'access_token' }).toString(),
      });
    }

    if (tokens.refreshToken) {
      await fetch(this.config.revocationEndpoint, {
        method: 'POST',
        headers,
        body: new URLSearchParams({ ...body, token: tokens.refreshToken, token_type_hint: 'refresh_token' }).toString(),
      });
    }
  }

  logout() {
    window.location = this.config.endSessionEndpoint;
  }
}